import React, { createContext, useContext, useState, useEffect } from 'react';
import { getActiveDiscounts } from '../firebase/services';
import { getBestDiscountForProduct, computeDiscountedPrice, formatDiscountBadge } from '../Utils/discounts';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('vetcare_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [discountsLoaded, setDiscountsLoaded] = useState(false);

  useEffect(() => {
    localStorage.setItem('vetcare_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const discounts = await getActiveDiscounts();
        if (!mounted) return;
        setActiveDiscounts(Array.isArray(discounts) ? discounts : []);
      } catch (e) {
        console.error('Failed to load active discounts', e);
        if (!mounted) return;
        setActiveDiscounts([]);
      } finally {
        if (mounted) setDiscountsLoaded(true);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const getCanonicalProductId = (item) => {
    if (!item) return null;
    const explicit = item.baseProductId || item.productId;
    if (explicit) return explicit;
    if (typeof item.id === 'string') return item.id.split('_')[0];
    return item.id;
  };

  const getCartItemPricing = (item) => {
    const baseUnitPrice = Number(item?.basePrice ?? item?.selectedPrice ?? item?.price ?? 0) || 0;
    if (!discountsLoaded || !Array.isArray(activeDiscounts) || activeDiscounts.length === 0) {
      return {
        baseUnitPrice,
        finalUnitPrice: baseUnitPrice,
        discount: null,
        badge: null
      };
    }

    const canonicalId = getCanonicalProductId(item);
    const productLike = {
      ...item,
      id: canonicalId
    };

    const best = getBestDiscountForProduct(productLike, activeDiscounts);
    const computed = computeDiscountedPrice(baseUnitPrice, best);

    return {
      baseUnitPrice,
      finalUnitPrice: computed.finalPrice,
      discount: best,
      badge: formatDiscountBadge(best)
    };
  };

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevItems, { ...product, quantity: quantity }];
    });
    setIsCartOpen(true); // Open cart when item is added
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const { finalUnitPrice } = getCartItemPricing(item);
      return total + (finalUnitPrice * (item.quantity || 1));
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const toggleCart = () => setIsCartOpen(!isCartOpen);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount,
      getCartItemPricing,
      isCartOpen,
      setIsCartOpen,
      toggleCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
